import { Component, Input, ViewChild, inject } from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AuditUniverseLinkableModel } from "@app/audit-universe/shared/data-models/audit-universe-linkable-model";
import { AuditDataService } from "@app/audit/services/audit-data.service";
import { AuditModel } from "@app/audit/shared/data-models/audit-model";
import { AuditContextConfigModel } from "@app/audit/shared/data-models/context/audit-context-config-model";
import { AuditContextStandingDataModel } from "@app/audit/shared/data-models/context/audit-context-standingdata-model";
import { AuditDetailPermissionModel } from "@app/audit/shared/permission-models/audit-detail-permission-model";
import { CerrixSelectTreeComponent } from "@app/shared/cerrix-select-tree/cerrix-select-tree.component";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { Pages } from "@constants/pages/Pages";
import { RendererType } from "@enums/RendererType";
import { getCustomFieldValueProperty } from "@methods/CustomFieldMethods";
import { nameof } from "@methods/jeffs-toolkit";
import { CustomFieldInfoModel } from "@models/customfields/CustomFieldInfoModel";
import { CustomFieldStandingDataModel } from "@models/customfields/CustomFieldStandingDataModel";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { injectTranslation, provideTranslationScope, TranslatePipe } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/audit/i18n/scope";
import { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";
import { CerrixCustomFieldsEditorComponent } from "@app/shared/components/cerrix-custom-fields-editor/cerrix-custom-fields-editor.component";

@Component({
    selector: "audit-detail-context",
    templateUrl: "./audit-detail-context.component.html",
    styleUrls: ["./audit-detail-context.component.scss"],
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        CerrixSelectTreeComponent,
        GenericListManagerComponent,
        CerrixCustomFieldsEditorComponent,
    ],
    viewProviders: [provideTranslationScope(scope)]
})
export class AuditDetailContextComponent {
    private _ds = inject(AuditDataService);
    private _toastr = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @Input() auditModel: AuditModel;
    @Input() permissions: AuditDetailPermissionModel;

    formGroup: FormGroup;
    auditUniverseConfig: GenericListConfig;
    customFieldData: { [key: number]: CustomFieldInfoModel } = {};

    standingData: AuditContextStandingDataModel;

    @ViewChild("orgTree") orgTree: CerrixSelectTreeComponent;
    @ViewChild("businessTree") businessTree: CerrixSelectTreeComponent;

    private _initExecuted = false;

    loaded = false;

    init(): void {
        if (this._initExecuted) {
            return;
        }

        this._initExecuted = true;
        this._ds.getAuditContextConfig(this.auditModel.guid).subscribe({
            next: async (configModel: AuditContextConfigModel) => {
                // The universe object list config below holds translated text.
                await this._translation.ready();
                this.initModel(configModel);

                this.createFormGroup();
                this.createAuditUniverseConfig();

                this.loaded = true;
            },
            error: () => {
                this._initExecuted = false;
            },
        });
    }

    private initModel(configModel: AuditContextConfigModel): void {
        this.standingData = configModel.standingData;

        this.auditModel.context = configModel.contextModel;
    }

    private createFormGroup(): void {
        const customFieldsFormArray = this.createCustomFieldsFormArray();

        this.formGroup = new FormGroup({
            organizationIds: new FormControl(
                this.auditModel.context.organizationIds,
                Validators.required
            ),
            businessDimensionIds: new FormControl(this.auditModel.context.businessDimensionIds),
            frameworkDimensionIds: new FormControl(this.auditModel.context.frameworkDimensionIds),
            customFields: customFieldsFormArray,
        });

        if (!this.permissions.canEditDetails) {
            this.formGroup.disable();
        } else {
            this.formGroup.valueChanges.subscribe(() => {
                FormValidationHelper.mapToModel(this.formGroup, this.auditModel.context);

                this.auditModel.context.contextValues = this.formGroup.value["customFields"];
            });
        }
    }

    public onSave(): void {
        if (this.formGroup) {
            FormValidationHelper.markAllAsTouched(this.formGroup);
        }
    }

    private createCustomFieldsFormArray(): FormArray<FormGroup> {
        const customFieldsFormArray = new FormArray([]);

        if (this.auditModel.id > 0) {
            this.standingData.contextFields.forEach((field: CustomFieldStandingDataModel) => {
                const answerField = this.auditModel.context.contextValues.find(
                    (x) => x.customFieldId === field.ID
                );

                const customFieldFormGroup = new FormGroup<any>({
                    ID: new FormControl(answerField ? answerField.ID : 0),
                    Name: new FormControl(field.Name),
                    customFieldId: new FormControl(field.ID),
                    required: new FormControl(field.required),
                });

                const valueProperty = getCustomFieldValueProperty(field);
                customFieldFormGroup.addControl(
                    valueProperty,
                    new FormControl(
                        answerField ? answerField[valueProperty] : null,
                        field.required ? [Validators.required] : []
                    )
                );

                // Store extra information from the custom field in a custom object which is easier accessible in the front-end
                this.customFieldData[field.ID] = {
                    Name: field.Name,
                    fieldType: field.fieldType,
                    required: field.required,
                    answerOptions: field.answerOptions,
                };

                customFieldsFormArray.push(customFieldFormGroup);
            });
        }

        return customFieldsFormArray;
    }

    private createAuditUniverseConfig(): void {
        if (!this.permissions.canViewLinkedAuditUniverse || this.auditModel.id <= 0) {
            return;
        }

        const linked = [];
        const unlinked = [];

        this.standingData.linkableUniverseObjects.forEach((d) => {
            if (d !== undefined && d !== null) {
                if (this.auditModel.context.linkedUniverseObjectIds.indexOf(d.id) >= 0) {
                    linked.push(d);
                } else {
                    unlinked.push(d);
                }
            }
        });

        this.auditUniverseConfig = <GenericListConfig>{
            name: this._translation.t("context.linkedUniverseObjectsTitle"),
            idProp: nameof<AuditUniverseLinkableModel>((x) => x.id),

            allowAdd: false,
            allowEdit: false,
            allowDelete: false,
            allowLink: this.permissions.canLinkAuditUniverse,

            isSortableByColumn: true,
            limitViewTo: 5,

            linkData: <any>{
                idValueProp: nameof<AuditUniverseLinkableModel>((x) => x.id),
                displayValueProp: nameof<AuditUniverseLinkableModel>((x) => x.name),
                unlinked,
                linkablePaginationItemCount: 15,
            },

            data: linked,
            fields: [
                {
                    prettyName: this._translation.t("context.nameColumn"),
                    fieldName: nameof<AuditUniverseLinkableModel>((x) => x.name),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this._translation.t("context.overallScoreColumn"),
                    fieldName: nameof<AuditUniverseLinkableModel>((x) => x.evaluatedRiskScore),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this._translation.t("context.dateColumn"),
                    fieldName: nameof<AuditUniverseLinkableModel>((x) => x.dateChanged),
                    fieldType: GenericListFieldType.Text,
                },
            ],

            rendererConfig: [
                {
                    actionColumn: nameof<AuditUniverseLinkableModel>(
                        (x) => x.evaluatedRiskScoreColor
                    ),
                    textColumn: nameof<AuditUniverseLinkableModel>((x) => x.evaluatedRiskScore),
                    type: RendererType.Score,
                },
                {
                    actionColumn: nameof<AuditUniverseLinkableModel>((x) => x.assessmentScoreColor),
                    textColumn: nameof<AuditUniverseLinkableModel>((x) => x.assessmentScore),
                    type: RendererType.Score,
                },
                {
                    type: RendererType.Score,
                    textColumn: "Evaluated Risk Score",
                    actionColumn: nameof<AuditUniverseLinkableModel>(
                        (x) => x.evaluatedRiskScoreColor
                    ),
                    hideActionColumn: true,
                },
                {
                    type: RendererType.Score,
                    textColumn: "Assessment Score",
                    actionColumn: nameof<AuditUniverseLinkableModel>((x) => x.assessmentScoreColor),
                    hideActionColumn: true,
                },
            ],

            overviewRowActionsName: this._translation.t("context.openAction"),
            overviewRowActions: [
                {
                    icon: "fas fa-external-link",
                    tooltip: this._translation.t("context.openAction"),
                    clickEvent: (item: AuditUniverseLinkableModel) => {
                        const guid = this.standingData.linkableUniverseObjects.find(
                            (x) => x.id === item.id
                        ).guid;
                        if (!guid) {
                            this._toastr.warning(
                                this._translation.t("context.couldNotOpenItem"),
                                this._translation.t("context.itemNotAvailable")
                            );
                        } else {
                            this._tabService.generateTab(this._pages.AuditUniverseDetail, guid);
                        }
                    },
                },
            ],

            onLinkedDataChange: async (data: AuditUniverseLinkableModel[]) => {
                const addedData = data.filter(
                    (uo) => !this.auditModel.context.linkedUniverseObjectIds.includes(uo.id)
                );

                this.auditModel.context.linkedUniverseObjectIds.splice(
                    0,
                    this.auditModel.context.linkedUniverseObjectIds.length
                );
                this.auditModel.context.linkedUniverseObjectIds.push(...data.map((d) => d.id));

                for (const uoLink of addedData) {
                    uoLink.organizationIds = uoLink.organizationIds.filter(
                        (oId) => !this.auditModel.context.organizationIds.includes(oId)
                    );
                    uoLink.businessDimensionIds = uoLink.businessDimensionIds.filter(
                        (bdId) => !this.auditModel.context.businessDimensionIds.includes(bdId)
                    );

                    const addOrganizations = uoLink.organizationIds.length > 0;
                    const addBusinessDimensions = uoLink.businessDimensionIds.length > 0;
                    const whatToAdd =
                        addOrganizations && addBusinessDimensions
                            ? "both"
                            : addOrganizations
                              ? "orgOnly"
                              : "bdOnly";

                    if (addOrganizations || addBusinessDimensions) {
                        const addOrgBd = await this._promptService
                            .confirmCustom({
                                maxWidth: "400px",
                                maxHeight: "350px",
                                data: {
                                    title: this._translation.t(
                                        "context.addingContextConfirmTitle",
                                        { what: whatToAdd }
                                    ),
                                    message: this._translation.t(
                                        "context.addingContextConfirmMessage",
                                        { what: whatToAdd, name: uoLink.name }
                                    ),
                                },
                            })
                            .toPromise();

                        if (addOrgBd) {
                            this.auditModel.context.organizationIds.push(...uoLink.organizationIds);
                            this.auditModel.context.businessDimensionIds.push(
                                ...uoLink.businessDimensionIds
                            );

                            if (uoLink.organizationIds.length > 0) {
                                this.orgTree.reload([...this.auditModel.context.organizationIds]);
                            }
                            if (uoLink.businessDimensionIds.length > 0) {
                                this.businessTree.reload([
                                    ...this.auditModel.context.businessDimensionIds,
                                ]);
                            }
                        }
                    }
                }
            },
        };
    }
}
